/**
 * Theme provider — dark / light / system colour-scheme management.
 *
 * Wraps the application in a React context that persists the user's theme
 * preference to `localStorage` and applies the corresponding class (`dark`
 * or `light`) to the document root element. When set to `system`, the
 * provider follows the OS-level `prefers-color-scheme` media query and
 * reacts to changes in real time.
 *
 * @module providers/theme
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Theme = 'dark' | 'light' | 'system';

interface ThemeProviderProps {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
}

interface ThemeProviderState {
	theme: Theme;
	setTheme: (theme: Theme) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveSystemTheme(): 'dark' | 'light' {
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
	const root = window.document.documentElement;
	root.classList.remove('light', 'dark');
	root.classList.add(theme === 'system' ? resolveSystemTheme() : theme);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

function ThemeProvider({ children, defaultTheme = 'system', storageKey = 'learnai-theme' }: ThemeProviderProps) {
	const [theme, setThemeState] = useState<Theme>(() => {
		if (typeof window === 'undefined') return defaultTheme;
		return (localStorage.getItem(storageKey) as Theme | null) ?? defaultTheme;
	});

	// Apply the theme class whenever the selection changes.
	useEffect(() => {
		applyTheme(theme);
	}, [theme]);

	// When in "system" mode, listen for OS-level colour-scheme changes.
	useEffect(() => {
		if (theme !== 'system') return;

		const mq = window.matchMedia('(prefers-color-scheme: dark)');
		const handler = () => applyTheme('system');

		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, [theme]);

	const value = useMemo<ThemeProviderState>(
		() => ({
			theme,
			setTheme: (next: Theme) => {
				localStorage.setItem(storageKey, next);
				setThemeState(next);
			},
		}),
		[theme, storageKey],
	);

	return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

function useTheme(): ThemeProviderState {
	const context = useContext(ThemeProviderContext);

	if (context === undefined) {
		throw new Error('useTheme must be used within a <ThemeProvider>');
	}

	return context;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { ThemeProvider, useTheme };
export type { Theme, ThemeProviderProps, ThemeProviderState };
